import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Container, Typography } from '@mui/material';
import LoginForm from './LoginForm';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const targetDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  // Původně 15.11. – prodlouženo o 14 dní
  const base = new Date(year, 10, 15, 0, 0, 0, 0);
  base.setDate(base.getDate() + 14);
  return base;
};

function formatRemaining(ms: number) {
  if (ms <= 0) return { d: 0, h: 0, m: 0, s: 0 };
  const totalSeconds = Math.floor(ms / 1000);
  const d = Math.floor(totalSeconds / (3600 * 24));
  const h = Math.floor((totalSeconds % (3600 * 24)) / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return { d, h, m, s };
}

const AuthGate: React.FC = () => {
  // Registration is disabled on gate page – login only
  const [now, setNow] = useState<Date>(new Date());
  const launch = useMemo(() => targetDate(), []);
  const { login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Capture intendedPath from URL on unauthenticated access
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const intended = params.get('intendedPath');
      const open = params.get('open');
      if (intended) {
        sessionStorage.setItem('intendedPath', decodeURIComponent(intended));
      } else if (open) {
        sessionStorage.setItem('intendedPath', `/dashboard?open=${open}`);
      }
    } catch {}
  }, [location.search]);

  const { d, h, m, s } = formatRemaining(launch.getTime() - now.getTime());

  const handleAuthSuccess = (user: any) => {
    login(user);
    try {
      const intended = sessionStorage.getItem('intendedPath');
      if (intended) {
        sessionStorage.removeItem('intendedPath');
        navigate(decodeURIComponent(intended));
        return;
      }
    } catch {}
    navigate('/dashboard');
  };

  const launchDisplay = `${launch.getDate()}. ${launch.getMonth() + 1}.`;

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', background: '#F2F2F2', display: 'flex', alignItems: 'center' }}>
      <Container maxWidth={false} sx={{ maxWidth: '900px', px: { xs: 2, sm: 3 }, py: { xs: 6, sm: 8 } }}>
        <Box sx={{
          p: { xs: 3, sm: 5 },
          background: '#FFFFFF',
          borderRadius: '24px',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
          textAlign: 'center'
        }}>
          <Typography sx={{ fontFamily: 'Inter', fontWeight: 800, fontSize: { xs: '24px', sm: '32px' }, mb: 2 }}>
            Přístup pouze pro přihlášené uživatele
          </Typography>
          <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: { xs: '14px', sm: '16px' }, color: '#444', mb: 3 }}>
            V tuto chvíli je PrávníBot v testování a není otevřen pro širokou veřejnost. Plánované spuštění je na {launchDisplay}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 3, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 120 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '28px' }}>{d}</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>dnů</Typography>
            </Box>
            <Box sx={{ minWidth: 120 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '28px' }}>{h}</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>hodin</Typography>
            </Box>
            <Box sx={{ minWidth: 120 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '28px' }}>{m}</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>minut</Typography>
            </Box>
            <Box sx={{ minWidth: 120 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '28px' }}>{s}</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>sekund</Typography>
            </Box>
          </Box>

          <Typography sx={{ color: '#777', mb: 3 }}>
            Přihlaste se pro přístup k testovací verzi.
          </Typography>

          <Box sx={{ maxWidth: 420, mx: 'auto', textAlign: 'left' }}>
            <LoginForm onSuccess={handleAuthSuccess} />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default AuthGate;
